import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { fetchCreatorHub } from "@/lib/library-hubs.functions";
import { creatorIntro, CREATOR_MIN_ITEMS } from "@/lib/library-creators";
import {
  LibraryHubPage,
  itemListJsonLd,
  breadcrumbJsonLd,
  datasetJsonLd,
} from "@/components/library-hub";
import { SiteFooter } from "@/components/site-footer";

export const Route = createFileRoute("/library/by/$creator")({
  loader: async ({ params }) => {
    try {
      return { hub: await fetchCreatorHub({ data: { creator: params.creator } }) };
    } catch {
      return { hub: null };
    }
  },
  head: ({ params, loaderData }) => {
    const hub = loaderData?.hub ?? null;
    const url = `https://vibing.inc/library/by/${params.creator}`;
    if (!hub) {
      return {
        meta: [
          { title: "Creator not found — The Vibing Library" },
          { name: "robots", content: "noindex" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const title = `${hub.name} — ${hub.count} featured ${
      hub.count === 1 ? "gem" : "gems"
    } | The Vibing Library`;
    const desc = creatorIntro(hub.name, hub.count, hub.categories).slice(0, 155);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: url },
        ...ogImageMeta("library", `Share card: gems by ${hub.name}`),
        { name: "twitter:card", content: "summary_large_image" },
        ...(hub.count < CREATOR_MIN_ITEMS
          ? [{ name: "robots", content: "noindex,follow" }]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: itemListJsonLd(`Gems by ${hub.name}`, url, hub.gems),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            name: hub.name,
            url,
            ...(hub.handle
              ? { sameAs: [`https://x.com/${hub.handle}`], alternateName: `@${hub.handle}` }
              : {}),
          }),
        },
        {
          type: "application/ld+json",
          children: datasetJsonLd({
            name: `Gems by ${hub.name} — The Vibing Library`,
            description: desc,
            url,
            count: hub.count,
            keywords: [hub.name, "creator", "curated tools"],
          }),
        },
        {
          type: "application/ld+json",
          children: breadcrumbJsonLd([
            { name: "Home", item: "https://vibing.inc/" },
            { name: "The Vibing Library", item: "https://vibing.inc/library" },
            { name: "Creators", item: "https://vibing.inc/library/creators" },
            { name: hub.name, item: url },
          ]),
        },
      ],
    };
  },
  component: CreatorHub,
});

function CreatorHub() {
  const { hub } = Route.useLoaderData();
  if (!hub) {
    return (
      <div className="min-h-dvh bg-background text-foreground">
        <main id="main" className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl">Creator not found</h1>
          <Link
            to="/library/creators"
            className="mt-4 inline-block text-muted-foreground underline"
          >
            Browse all creators
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }
  return (
    <LibraryHubPage
      eyebrow={hub.handle ? `The Vibing Library — @${hub.handle}` : "The Vibing Library — creator"}
      title={hub.name}
      intro={creatorIntro(hub.name, hub.count, hub.categories)}
      gems={hub.gems}
      crumbs={[
        { label: "Home", to: "/" },
        { label: "The Vibing Library", to: "/library" },
        { label: "Creators", to: "/library/creators" },
        { label: hub.name },
      ]}
    />
  );
}
